import {
  estaEncerrada,
  getConversationsList,
  postConversationReopen,
  postConversations,
  postPresenceEnsure,
} from './conversationsApi'
import { acharConversaDoPar } from './acharConversaDoPar'
import {
  ConversationsApiError,
  describeConversationsError,
  toConversationsApiError,
} from './conversationsApiError'
import type { ConversationAuthPair } from '../../utils/conversationUserAuthHeader'
import {
  fetchAndActivateRemoteConfig,
  getConversationAutoReopenFromRemote,
} from '../../configs/firebase'

/**
 * Prepara a conversa do par ao **iniciar o atendimento**.
 *
 * Sem isto, a conversa só nasce quando o profissional abre a aba de chat — e,
 * com presença por demanda (Fase 4), ele só vira participante nesse momento.
 * Um paciente que mandasse mensagem logo no começo escreveria para uma conversa
 * onde o profissional ainda não está.
 *
 * Iniciar o atendimento é o ponto natural para resolver isso: é quando o
 * profissional declara que está atendendo.
 *
 * ## Ordem: consultar o par antes de escrever
 *
 * Esta função criava a conversa primeiro e só depois checava `closedAt`. Não
 * funcionava: com a conversa encerrada, `POST /v1/conversations` responde
 * **409 CONVERSATION_CLOSED**, a chamada levanta, e o fluxo cai no catch
 * silencioso abaixo. Consequências, ambas invisíveis:
 *
 * 1. a reabertura nunca era alcançada — ela dependia de a criação ter sucesso
 *    **e** devolver conversa encerrada, que é exatamente o caso que dá 409;
 * 2. o `presence/ensure` não rodava, então o profissional não virava
 *    participante e o chat não funcionava no atendimento.
 *
 * Eram 155 respostas 409 por dia neste cliente. Agora o GET da listagem por par
 * vem primeiro, e a escrita acontece com o estado já conhecido.
 *
 * ## Reabrir aqui é o gesto certo, independente de quem encerrou
 *
 * A versão anterior só reabria `closedBy: 'SYSTEM'`, com o argumento de que
 * encerramento por pessoa é decisão de alguém. O argumento vale em geral, mas
 * não neste ponto: quem chama isto é o clique em "iniciar atendimento" — um
 * gesto explícito do profissional, numa consulta **nova**. O encerramento
 * anterior tem `reason: atendimento_concluido` e pertence à consulta passada;
 * não faz sentido ele bloquear a próxima.
 *
 * Por isso a reabertura deixou de olhar `closedBy`. O que autoriza é o
 * contexto, não quem fechou. Decisão de produto de 27/08/2026.
 *
 * Atenção: o código também **não lê `closeReason`**. A justificativa acima vale
 * para `atendimento_concluido`; um encerramento feito pelo paciente por outro
 * motivo seria desfeito do mesmo jeito. Validar com Produto e DPO antes de ligar
 * a flag e, se precisar, restringir por `closedBy`/`closeReason`.
 *
 * ⚠️ Continua atrás de `conversation_auto_reopen`, cujo **default é `false`**
 * (`configs/firebase.ts`). Sem a chave ligada no Remote Config, o
 * comportamento novo não acontece — o atendimento começa com o chat em modo
 * encerrado e devolve `'encerrada'`.
 *
 * **Nunca lança.** O atendimento já foi iniciado quando isto roda; falhar aqui
 * só adia a criação da conversa para quando a aba de chat abrir, que é o
 * comportamento de hoje. Derrubar o início da consulta por causa do chat seria
 * inaceitável.
 */
export async function prepararConversaDoPar(
  personId: string | undefined,
  professionalId: string | undefined
): Promise<'pronta' | 'somente-leitura' | 'encerrada' | 'sem-par' | 'falhou'> {
  const person = (personId ?? '').trim()
  const professional = (professionalId ?? '').trim()
  if (!person || !professional) return 'sem-par'

  const pair: ConversationAuthPair = {
    personId: person,
    professionalId: professional,
  }

  // Mesma razão do `closeOnFinish`: o getter é síncrono e vale o default até o
  // fetch completar. A promessa é memoizada, então não há fetch extra.
  await fetchAndActivateRemoteConfig().catch(() => undefined)

  try {
    /**
     * Estado do par por GET, sem escrever nada.
     *
     * Só o **404** vira lista vazia: o serviço o devolve quando o par não tem
     * conversa, e tratar como erro faria a primeira consulta do par virar falha.
     * Qualquer outro erro (5xx, rede) propaga — sem saber o estado do par, criar
     * poderia dar 409 numa conversa encerrada e esconder o problema.
     */
    const lista = await getConversationsList(pair, {
      person_id: person,
      professional_id: professional,
    }).catch((e: unknown) => {
      if (e instanceof ConversationsApiError && e.status === 404) return []
      throw e
    })

    const existente = acharConversaDoPar(lista, person, professional)

    let conversationId = existente?.id ? String(existente.id) : ''

    /**
     * Encerrada: reabrir **antes** da presença, porque o `presence/ensure`
     * responde 409 enquanto `closedAt` estiver preenchido.
     *
     * Uma tentativa só — falha deixa o chat em modo encerrado, nunca vira laço.
     */
    if (existente && estaEncerrada(existente)) {
      if (!conversationId) return 'encerrada'
      if (!getConversationAutoReopenFromRemote()) return 'encerrada'

      const reaberta = await postConversationReopen(conversationId, pair).catch(
        () => null
      )
      if (!reaberta || estaEncerrada(reaberta)) return 'encerrada'
    }

    /**
     * Cria só quando o par não tem conversa. Aqui já se sabe que não há
     * encerramento pendente, então o POST não pode levar 409 por esse motivo.
     */
    if (!conversationId) {
      const criada = await postConversations(
        { personId: person, professionalId: professional },
        pair
      )
      conversationId = criada?.id ? String(criada.id) : ''
    }

    if (!conversationId) return 'falhou'

    const presenca = await postPresenceEnsure(conversationId, pair)
    return presenca.professionalPresence === 'ACTIVE'
      ? 'pronta'
      : 'somente-leitura'
  } catch (e) {
    // Silencioso de propósito: ver docstring. A aba de chat ainda vai tentar.
    // Só a allowlist: o erro bruto carrega `serverMessage` e config do axios.
    console.warn(
      '[Chat] não foi possível preparar a conversa ao iniciar',
      describeConversationsError(toConversationsApiError(e))
    )
    return 'falhou'
  }
}
